
import React from "react";
import { Box, Container, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SecurityIcon from "@mui/icons-material/Security";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTheme } from "@emotion/react";

const features = [
    {
        title: "Financial Services",
        desc: "Apply for credit cards from top banks, compare benefits quickly, and manage everything securely with paperless support.",
        icon: <AccountBalanceIcon />,
    },
    {
        title: "Investments",
        desc: "Start SIPs or lump-sum investments in mutual funds with goal-based guidance and smart tools for better growth.",
        icon: <TrendingUpIcon />,
    },
    {
        title: "Insurance",
        desc: "Explore investment-linked insurance plans, service benefits, and smart banking solutions curated for clarity and convenience.",
        icon: <SecurityIcon />,
    },
    {
        title: "Service Benefits",
        desc: "Get in-app support, FAQs, financial guides, and updates to make your financial life simpler, faster, and stress-free.",
        icon: <DonutLargeIcon />,
    },
];

const Features = () => {
    const theme = useTheme();

    return (
        <Box sx={{ py: 4, position: "relative", overflow: "hidden" }}>
            <Container maxWidth="lg">
                <Box
                    sx={{
                        mb: { xs: 3, md: 4 },
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "13px",
                            letterSpacing: "2px",
                            fontWeight: 600,
                            mb: 1,
                            color: theme.palette.primary.main,
                        }}
                        data-aos="fade-right"
                    >
                        • CORE SERVICES
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: "20px", sm: "28px", md: "40px" },
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            position: "relative",
                            display: "inline-block",
                        }}
                    >
                        More{" "}
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 900,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.text.primary})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                position: "relative",
                                display: "inline-block",
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    bottom: "-4px",
                                    left: 0,
                                    width: "100%",
                                    height: "2px",
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                                },
                            }}
                        >
                            Additional
                        </Box>{" "}
                        Features
                    </Typography>
                </Box>

                {/* CARDS */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                            md: "1fr 1fr",
                        },
                        gap: 4,
                        mb: 10,
                        width: "100%",
                    }}
                >
                    {features.map((item, index) => (
                        <FeatureCard key={index} item={item} index={index} />
                    ))}
                </Box>

                <Box
                    sx={{
                        position: "absolute",
                        top: "75%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "100%",
                        display: { xs: "none", md: "flex" },
                        justifyContent: "center",
                        gap: 8,
                        zIndex: 0,
                        pointerEvents: "none",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: 40, md: 100 },
                            fontWeight: 700,
                            color: "#e4e8f4",
                            letterSpacing: 18,
                            transform: "translateX(-70px)",
                        }}
                    >
                        Arth
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: 40, md: 100 },
                            fontWeight: 700,
                            color: "#e4e8f4",
                            letterSpacing: 10,
                            transform: "translateX(100px)",
                        }}
                    >
                        Gyan
                    </Typography>
                </Box>

                {/* PHONE IMAGE */}
                <Box
                    data-aos="zoom-in"
                    data-aos-duration="1000"
                    data-aos-easing="ease-out-cubic"
                    sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        zIndex: 2,
                        mt: -6,
                    }}
                >
                    <Box
                        component="img"
                        src="https://ik.imagekit.io/arthgyan/Arthgyan_Web/app%20screens/features"
                        alt="Mobile UI"
                        sx={{
                            width: { xs: 360, md: 480 },
                            display: "block",
                        }}
                    />

                    {/* BOTTOM FADE EFFECT */}
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            width: "100%",
                            height: "25%",
                            background: `linear-gradient(
  to bottom,
  ${theme.palette.primary.main}00 0%,
  ${theme.palette.primary.main}22 60%,
  ${theme.palette.primary.main}33 100%
)`,
                        }}
                    />
                </Box>
            </Container>
        </Box>
    );
};

const FeatureCard = ({ item: baseItem, index }) => {
    const theme = useTheme();
    const number = String(index + 1).padStart(2, "0");

    const color = theme.palette.primary.main;
    const item = {
        ...baseItem,
        iconColor: color,
        cardBg: `${color}14`,
    };

    return (
        <Box
            data-aos="fade-up"
            data-aos-delay={index * 80}
            sx={{
                position: "relative",
                overflow: "hidden",
                width: "100%",
                display: "flex",
                alignItems: "flex-start",
                gap: 2.5,
                p: { xs: "22px", md: "28px" },
                borderRadius: "22px",
                cursor: "pointer",
                background: `linear-gradient(135deg, ${item.cardBg} 0%, #ffffff 85%)`,
                border: `1px solid ${item.iconColor}22`,
                boxShadow: "0 6px 20px rgba(17,24,39,0.05)",
                transition: "all 0.4s cubic-bezier(0.22, 1, 0.36, 1)",

                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "4px",
                    height: "35%",
                    borderRadius: "0 4px 4px 0",
                    background: item.iconColor,
                    transition: "height 0.4s ease",
                },

                "&:hover": {
                    transform: "translateY(-8px)",
                    borderColor: `${item.iconColor}55`,
                    boxShadow: `0 22px 44px ${item.iconColor}30`,
                },
                "&:hover::before": {
                    height: "70%",
                },
                "&:hover .fc-icon": {
                    background: item.iconColor,
                    color: "#ffffff",
                    transform: "rotate(-6deg) scale(1.08)",
                    boxShadow: `0 12px 24px ${item.iconColor}55`,
                },
                "&:hover .fc-arrow": {
                    opacity: 1,
                    transform: "translateX(0)",
                },
                "&:hover .fc-number": {
                    color: `${item.iconColor}33`,
                },
            }}
        >
            <Typography
                className="fc-number"
                sx={{
                    position: "absolute",
                    top: 6,
                    right: 20,
                    fontSize: 64,
                    fontWeight: 800,
                    lineHeight: 1,
                    color: `${item.iconColor}18`,
                    userSelect: "none",
                    transition: "color 0.4s ease",
                }}
            >
                {number}
            </Typography>

            {/* Icon */}
            <Box
                className="fc-icon"
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: 56,
                    height: 56,
                    borderRadius: "16px",
                    background: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: item.iconColor,
                    boxShadow: `0 8px 18px ${item.iconColor}25`,
                    flexShrink: 0,
                    transition: "all 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
                    "& svg": { fontSize: 26 },
                }}
            >
                {item.icon}
            </Box>

            {/* Text */}
            <Box sx={{ position: "relative", zIndex: 1, pr: { xs: 0, md: 4 } }}>
                <Typography
                    sx={{
                        fontSize: "17px",
                        fontWeight: 700,
                        color: "#111827",
                        mb: 0.8,
                    }}
                >
                    {item.title}
                </Typography>

                <Typography
                    sx={{
                        fontSize: "13.5px",
                        color: "#5B6475",
                        lineHeight: 1.65,
                    }}
                >
                    {item.desc}
                </Typography>

                {/* Hover arrow */}
                <Box
                    className="fc-arrow"
                    sx={{
                        mt: 1.5,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: "13px",
                        fontWeight: 600,
                        color: item.iconColor,
                        opacity: 0,
                        transform: "translateX(-10px)",
                        transition: "all 0.4s ease",
                    }}
                >
                    Learn more <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Box>
            </Box>
        </Box>
    );
};

export default Features;
